// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
} as const;

// Current log level - can be adjusted based on environment
const CURRENT_LOG_LEVEL = import.meta.env.PROD ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;

// Maximum number of logs to keep in local storage
const MAX_LOCAL_LOGS = 100;

// Local storage key for logs
const LOGS_STORAGE_KEY = 'kitlin_app_logs';

// Utility to format objects for logging
const formatData = (data: any): string => {
  try {
    if (!data) return '';
    
    // Handle Error objects specially
    if (data instanceof Error) {
      return JSON.stringify({
        message: data.message,
        name: data.name,
        stack: data.stack,
        ...(data as any) // Include any other custom properties
      }, null, 2);
    }
    
    return JSON.stringify(data, null, 2);
  } catch (err) {
    return String(data);
  }
};

// Timestamp formatter
const getTimestamp = (): string => {
  return new Date().toISOString();
};

// Error formatter with more detailed properties extraction
const formatError = (error: any): Record<string, any> => {
  if (!error) return { message: 'Unknown error' };
  
  // Handle Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: import.meta.env.DEV ? error.stack : undefined,
      ...(error as any) // Include any custom properties
    };
  }
  
  // Handle Supabase or other API errors
  return {
    message: error.message || error.error_description || error.msg || 'Unknown error',
    name: error.name || error.code || 'Error',
    code: error.code || error.statusCode,
    details: error.details || error.error_description,
    hint: error.hint,
    stack: import.meta.env.DEV ? error.stack : undefined,
    status: error.status || error.statusCode,
    statusText: error.statusText,
    response: error.response,
  };
};

// Save logs to local storage for debugging
const saveLogToLocalStorage = (level: string, message: string, details: any = null) => {
  try {
    // Load existing logs
    const existingLogsString = localStorage.getItem(LOGS_STORAGE_KEY) || '[]';
    const existingLogs = JSON.parse(existingLogsString);
    
    // Add new log
    const newLog = {
      timestamp: getTimestamp(),
      level,
      message,
      details: details ? (typeof details === 'string' ? details : JSON.stringify(details)) : null
    };
    
    // Keep only the most recent logs
    const updatedLogs = [newLog, ...existingLogs].slice(0, MAX_LOCAL_LOGS);
    
    // Save back to storage
    localStorage.setItem(LOGS_STORAGE_KEY, JSON.stringify(updatedLogs));
  } catch (e) {
    // Silently fail if local storage is not available
    console.error('Could not save log to local storage', e);
  }
};

// Send logs to Supabase with retry mechanism
const sendLogToServer = async (level: string, message: string, details: any = {}, retryCount = 0) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    // Enhanced validation of environment variables
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('⚠️ Missing Supabase environment variables, skipping server logging');
      return;
    }
    
    // Only send errors and warnings to the server in production
    if (import.meta.env.PROD && level !== 'ERROR' && level !== 'WARN') {
      return;
    }

    // Ensure URL ends with a trailing slash before appending the function path
    const baseUrl = supabaseUrl.endsWith('/') ? supabaseUrl : `${supabaseUrl}/`;
    const functionUrl = new URL('functions/v1/client-log', baseUrl).toString();

    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          level: level.toLowerCase(),
          message,
          details,
          source: 'client',
          client_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            url: window.location.href,
            timestamp: new Date().toISOString()
          }
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Retry logic for network errors
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return sendLogToServer(level, message, details, retryCount + 1);
      }
      
      // Only log as warning since this is auxiliary functionality
      console.warn('⚠️ Failed to send log to server after retries:', 
        fetchError instanceof Error ? fetchError.message : String(fetchError)
      );
    }
  } catch (e) {
    // Don't throw errors for logging failures
    console.warn('Failed to send log to server', e);
  }
};

export const logger = {
  debug: (message: string, data?: any) => {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.DEBUG) {
      const formattedData = data ? formatData(data) : '';
      console.debug(
        `[DEBUG] [${getTimestamp()}] ${message}`,
        formattedData ? formattedData : ''
      );
      saveLogToLocalStorage('DEBUG', message, data);
    }
  },

  info: (message: string, data?: any) => {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.INFO) {
      const formattedData = data ? formatData(data) : '';
      console.log(
        `[INFO] [${getTimestamp()}] ${message}`,
        formattedData ? formattedData : ''
      );
      saveLogToLocalStorage('INFO', message, data);
      
      // Make server logging optional and non-blocking
      void sendLogToServer('INFO', message, data);
    }
  },

  warn: (message: string, data?: any) => {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.WARN) {
      const formattedData = data ? formatData(data) : '';
      console.warn(
        `[WARN] [${getTimestamp()}] ${message}`,
        formattedData ? formattedData : ''
      );
      saveLogToLocalStorage('WARN', message, data);
      
      // Make server logging optional and non-blocking
      void sendLogToServer('WARN', message, data);
    }
  },

  error: (message: string, error: any) => {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.ERROR) {
      const formattedError = formatError(error);
      console.error(
        `[ERROR] [${getTimestamp()}] ${message}`,
        formattedError
      );
      saveLogToLocalStorage('ERROR', message, formattedError);
      
      // Make server logging optional and non-blocking
      void sendLogToServer('ERROR', message, formattedError);

      // In development, also log the full error object
      if (import.meta.env.DEV) {
        console.error('Original error:', error);
      }
    }
  },

  // Group related logs
  group: (name: string, fn: () => void) => {
    console.group(`[GROUP] [${getTimestamp()}] ${name}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },

  // Measure execution time
  time: async <T>(label: string, fn: () => Promise<T>): Promise<T> => {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      logger.info(`${label} completed in ${duration.toFixed(2)}ms`);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      logger.error(`${label} failed after ${duration.toFixed(2)}ms`, error);
      throw error;
    }
  },

  // Get logs from local storage
  getLogs: () => {
    try {
      const logsString = localStorage.getItem(LOGS_STORAGE_KEY);
      return logsString ? JSON.parse(logsString) : [];
    } catch (e) {
      console.error('Error retrieving logs', e);
      return [];
    }
  },

  // Clear logs
  clearLogs: () => {
    try {
      localStorage.removeItem(LOGS_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing logs', e);
    }
  }
};

// Add global error handling to capture uncaught errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', event.reason);
  });
}

// Prevent console logging in production
if (import.meta.env.PROD) {
  console.debug = () => {};
}